import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dispatch, SetStateAction, useContext, useEffect } from 'react';
import { checkActionSubPermission, toTypeCase } from '../../../../../utils/common';
import MlxActionIcon from '../../../../../components/common/MlxActionIcon/MlxActionIcon';
import MlxInputSwitch from '../../../../../components/common/MlxInputSwitch/MlxInputSwitch';
import { useMutation, useQueryClient, UseQueryResult } from 'react-query';
import { _DatatablePageEvent, _Venue } from '../../../../../constants/staticTypes';
import logger from '../../../../../utils/logger';
import HttpServiceHelper from '../../../../../helpers/httpServiceHelper';
import MlxBlockUI from '../../../../../components/common/MlxBlockUI/MlxBlockUI';
import MlxConfirmPopup from '../../../../../components/common/MlxConfirmPopup/MlxConfirmPopup';
import { confirmPopup } from 'primereact/confirmpopup';
import { CONFIRM_VENUE_DELETE, CONFIRM_Venue_STATUS_CHANGE } from '../../../../../constants/strings';
import { useTranslation } from '../../../../../hooks/useTranslation';
import { AxiosResponse } from 'axios';
import {
    _VenueFormType,
    deleteVenueByID,
    getVenueDetailByID,
    invalidateVenueOptions,
    updateVenueStatus,
    venueCreationStatus,
} from '../../../../../helpers/venueHelper';
import { OrgPermissionKeys, OrgSubPermissionKeys } from '../../../../../constants/constants';
import { AuthCtx } from '../../../../../context/AuthCtxProvider';
import MlxToolTip from '../../../../../components/common/MlxToolTip/MlxToolTip';

type Props = {
    dataTableEvent: _DatatablePageEvent;
    setDataTableEvent: Dispatch<SetStateAction<_DatatablePageEvent>>;
    openEditVenueSidebar: (data: _VenueFormType) => void;
    venueListData: _Venue[];
    setVenueListData: Dispatch<SetStateAction<_Venue[]>>;
    venueListQuery: UseQueryResult<AxiosResponse<any, any>, unknown>;
};

const languageTexts = {
    CONFIRM_VENUE_DELETE: CONFIRM_VENUE_DELETE,
    CONFIRM_Venue_STATUS_CHANGE: CONFIRM_Venue_STATUS_CHANGE,
};

function VenueList({
    dataTableEvent,
    setDataTableEvent,
    openEditVenueSidebar,
    venueListData,
    setVenueListData,
    venueListQuery,
}: Readonly<Props>) {
    const translatedText = useTranslation(languageTexts, 'none');
    const getVenueByIDMutation = useMutation(HttpServiceHelper);
    const venueCreationStatusMutation = useMutation(HttpServiceHelper);
    const queryClient = useQueryClient();
    const { userType } = useContext(AuthCtx);

    const handleVenueStatusChange = (rowData: any, event: any) => {
        updateVenueStatus(
            getVenueByIDMutation,
            { id: rowData?.id, newStatus: !event.target.checked },
            (data) => {
                invalidateVenueOptions(queryClient);
                // Update the isActive field in the state
                setVenueListData((prevData: any) =>
                    prevData.map((staff: any) => (staff.id === rowData.id ? { ...staff, isActive: !staff.isActive } : staff))
                );
            },
            (error) => logger.log('Failed to update the status', error)
        );
    };

    const confirmStatusChange = (event: any, rowData: any) => {
        confirmPopup({
            target: event.target,
            message: translatedText.CONFIRM_Venue_STATUS_CHANGE,
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => handleVenueStatusChange(rowData, event),
            reject: () => {},
        });
    };

    const statusTemplateWithAction = (rowData: any) => {
        return (
            <div className="d-flex flex-row align-items-center justify-content-between" style={{ maxWidth: '110px', minWidth: '110px' }}>
                <span className="me-2">{toTypeCase(rowData.isActive ? 'Active' : 'Inactive', 'capitalize')}</span>
                <MlxInputSwitch
                    name="admin-staff-status"
                    checked={rowData.isActive}
                    onClick={(event) => confirmStatusChange(event, rowData)}
                />
            </div>
        );
    };

    const handleVenueEdit = (rowData: any) => {
        getVenueDetailByID(
            getVenueByIDMutation,
            { id: rowData?.id },
            (data) => {
                let venueData = data?.data;
                const editableData: _VenueFormType = { ...venueData, domainName: venueData?.domainName?.split('.')[0] ?? '' };
                openEditVenueSidebar(editableData);
            },
            (error) => logger.log('Failed to fetch user details', error)
        );
    };

    const confirmDelete = (event: any, rowData: any) => {
        confirmPopup({
            target: event.currentTarget,
            message: translatedText.CONFIRM_VENUE_DELETE,
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => handleVenueDelete(rowData),
            reject: () => {},
        });
    };

    const handleVenueDelete = (rowData: any) => {
        deleteVenueByID(
            getVenueByIDMutation,
            { id: rowData?.id },
            () => {
                invalidateVenueOptions(queryClient);
                venueListQuery.refetch();
            },
            (error) => logger.log('Failed to delete admin staff', error)
        );
    };

    const actionTemplate = (rowData: any) => {
        return (
            <div className="d-flex flex-row align-items-center justify-content-end" style={{ minWidth: '70px' }}>
                {(userType === 'organizationAdmin' ||
                    checkActionSubPermission(queryClient, OrgPermissionKeys.MANAGE_VENUE, OrgSubPermissionKeys.EDIT_VENUE)) && (
                    <MlxActionIcon className="me-3 edit-icon-selection" actionType="EDIT" onClick={() => handleVenueEdit(rowData)} />
                )}
                {(userType === 'organizationAdmin' ||
                    checkActionSubPermission(queryClient, OrgPermissionKeys.MANAGE_VENUE, OrgSubPermissionKeys.DELETE_VENUE)) && (
                    <MlxActionIcon
                        className="me-3 delete-icon-selection"
                        actionType="DELETE"
                        onClick={(event) => confirmDelete(event, rowData)}
                    />
                )}
            </div>
        );
    };

    const venueNameClicked = (rowData: any) => {
        if (rowData.domainName) {
            let url = rowData.domainName;
            if (!/^https?:\/\//i.test(url)) {
                url = 'https://' + url;
            }
            window.open(url, '_blank');
        }
    };

    // If there is any subdomain pending status, call check-multiple-domain-status API with their IDs, then refetch venue list
    useEffect(() => {
        const interval = setInterval(() => {
            const pendingVenues = venueListData?.filter((venue) => venue.subDomainStatus === 'PENDING');
            if (pendingVenues.length > 0) {
                const ids = pendingVenues.map((venue) => venue.id);
                // Call the check-multiple-domain-status API
                venueCreationStatus(
                    venueCreationStatusMutation,
                    { ids },
                    (res) => {
                        // Update venueListData with the new status from the response
                        const updatedVenues = res.data.venues;
                        if (updatedVenues && updatedVenues.length > 0) {
                            setVenueListData((prevData) =>
                                prevData.map((venue) => {
                                    const updatedVenue = updatedVenues.find((uv: any) => uv.venueId === venue.id);
                                    if (updatedVenue) {
                                        return { ...venue, subDomainStatus: updatedVenue.currentStatus };
                                    }
                                    return venue;
                                })
                            );
                        }
                    },
                    (error) => logger.log('Failed to check venue creation status', error)
                );
            }
        }, 30000);
        return () => clearInterval(interval);
    }, [venueListData, venueListQuery]);

    return (
        <MlxBlockUI isLoading={getVenueByIDMutation.isLoading || venueListQuery.isRefetching || venueListQuery.isLoading} opacity={0.5}>
            <MlxConfirmPopup />
            <DataTable
                className="custom-p-table"
                value={venueListData}
                paginator
                rows={dataTableEvent.rows}
                totalRecords={dataTableEvent.totalRows}
                first={dataTableEvent.first}
                rowsPerPageOptions={dataTableEvent.rowsPerPageOptions}
                size="small"
                removableSort
                scrollable
                scrollHeight="flex"
                paginatorTemplate={dataTableEvent.paginatorTemplate}
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
                dataKey="id"
                onPage={(event) => setDataTableEvent({ ...dataTableEvent, ...event, forceUpdate: dataTableEvent.forceUpdate + 1 })}
                lazy
            >
                <Column
                    style={{ maxWidth: '12rem', minWidth: '12rem' }}
                    header="Venue Name"
                    field="tenantName"
                    body={(rowData) => (
                        <div className="d-flex orgname-contanier">
                            <span className="org-name-link me-1" onClick={() => venueNameClicked(rowData)}>
                                {toTypeCase(rowData?.tenantName ?? '', 'capitalize')}
                            </span>
                            {rowData?.subDomainStatus === 'PENDING' ? (
                                <MlxToolTip target={`.domainName-${rowData.id}`}>
                                    <div
                                        className={`domainName-${rowData.id} text-warning`}
                                        data-pr-tooltip={'Pending Venue Creation'}
                                        data-pr-position="bottom"
                                    >
                                        <i className="pi pi-spin pi-spinner text-warning"></i>
                                    </div>
                                </MlxToolTip>
                            ) : (
                                <span className="me-2">
                                    <i className="pi pi-link"></i>
                                </span>
                            )}
                        </div>
                    )}
                ></Column>
                <Column
                    style={{ maxWidth: '12rem', minWidth: '12rem' }}
                    header="Email"
                    field="email"
                    body={(rowData) => (
                        <MlxToolTip target={`.staffEmail-${rowData.id}`}>
                            <div
                                className={`staffEmail-${rowData.id} overflow-ellipsis`}
                                data-pr-tooltip={rowData?.email}
                                data-pr-position="bottom"
                            >
                                {rowData?.email}
                            </div>
                        </MlxToolTip>
                    )}
                ></Column>
                <Column style={{ maxWidth: '12rem', minWidth: '12rem' }} header="Phone" field="primaryPhone"></Column>
                <Column style={{ maxWidth: '12rem', minWidth: '12rem' }} header="Zip Code" field="zip"></Column>
                <Column style={{ minWidth: '110px' }} header="Status" body={statusTemplateWithAction}></Column>
                {(userType === 'organizationAdmin' ||
                    checkActionSubPermission(queryClient, OrgPermissionKeys.MANAGE_VENUE, OrgSubPermissionKeys.EDIT_VENUE) ||
                    checkActionSubPermission(queryClient, OrgPermissionKeys.MANAGE_VENUE, OrgSubPermissionKeys.DELETE_VENUE)) && (
                    <Column header="" style={{ minWidth: '70px' }} body={actionTemplate}></Column>
                )}
            </DataTable>
        </MlxBlockUI>
    );
}

export default VenueList;
